import styled from "styled-components";
import { IndicatorsReportViewModel } from "../../../../domain/viewmodels/report/IndicatorsReportViewModel";
import { SortableTableHeader } from "./SortableTableHeader";
import { LoadingSpinner } from "./LoadingSpinner";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";

interface IndicatorsReportTableProps {
    data: IndicatorsReportViewModel[];
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSort: (column: string) => void;
    isLoading: boolean;
    error: string | null;
    onRetry: () => void;
}

const columns: { label: string; field: keyof IndicatorsReportViewModel; className?: string }[] = [
    { label: "Nome Aluno", field: "studentName", className: "col-name" },
    { label: "Email do Aluno", field: "studentEmail", className: "col-email" },
    { label: "Titulo do Curso", field: "courseTitle", className: "col-course" },
    { label: "Titulo do Modulo", field: "moduleTitle", className: "col-module" },
    { label: "Último Conteúdo Acessado ou Concluído", field: "lastContentAccessed", className: "col-content" },
    { label: "Nota Prova", field: "examGrade", className: "col-grade" },
    { label: "Data de Início do Módulo", field: "moduleStartDate", className: "col-date" },
    { label: "Data de Término do Módulo", field: "moduleEndDate", className: "col-date" },
    { label: "Status de Conclusão do Módulo", field: "moduleCompletionStatus", className: "col-status" },
    { label: "Status do Progresso de Aprendizagem", field: "learningProgressStatus", className: "col-status" },
    { label: "Tempo Gasto", field: "timeSpent", className: "col-time" },
    { label: "N° Acessos Gerais", field: "generalAccessCount", className: "col-access" },
];

export function IndicatorsReportTable({
    data,
    sortBy,
    sortDirection,
    onSort,
    isLoading,
    error,
    onRetry
}: IndicatorsReportTableProps) {
    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorState message={error} onRetry={onRetry} />;
    }

    if (data.length === 0) {
        return <EmptyState />;
    }

    return (
        <TableWrapper>
            <div className="scroll-container">
                <table className="indicators-report-table">
                    <thead>
                        <tr>
                            {columns.map(col => (
                                <SortableTableHeader
                                    key={col.field}
                                    label={col.label}
                                    column={col.field}
                                    className={col.className}
                                    currentSortBy={sortBy}
                                    currentSortDirection={sortDirection}
                                    onSort={onSort}
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.map(row => (
                            <tr key={`${row.studentEmail}-${row.moduleTitle}-${row.moduleOrder}`}>
                                <td className="col-name">{row.studentName}</td>
                                <td className="col-email" title={row.studentEmail}>{row.studentEmail}</td>
                                <td className="col-course">{row.courseTitle}</td>
                                <td className="col-module">{row.moduleTitle}</td>
                                <td className="col-content">{row.lastContentAccessed}</td>
                                <td className="col-grade">{row.examGrade}</td>
                                <td className="col-date">{row.moduleStartDate}</td>
                                <td className="col-date">{row.moduleEndDate}</td>
                                <td className="col-status">{row.moduleCompletionStatus}</td>
                                <td className="col-status">{row.learningProgressStatus}</td>
                                <td className="col-time">{row.timeSpent}</td>
                                <td className="col-access">{row.generalAccessCount}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </TableWrapper>
    );
}

const TableWrapper = styled.div`
    margin-top: 0;

    .scroll-container {
        overflow-x: auto;
        overflow-y: auto;
        min-height: 70vh;
        max-height: 70vh;
    }

    .indicators-report-table {
        font-family: Lato;
        font-size: 0.85rem;
        min-width: 1800px;
        width: 100%;
        border-collapse: separate;
        border-spacing: 0 6px;
        table-layout: fixed;

        .col-name    { width: 150px; }
        .col-email   { width: 200px; }
        .col-course  { width: 160px; }
        .col-module  { width: 150px; }
        .col-content { width: 160px; }
        .col-grade   { width: 100px; }
        .col-date    { width: 130px; }
        .col-status  { width: 130px; }
        .col-time    { width: 110px; }
        .col-access  { width: 110px; }

        thead {
            position: sticky;
            top: 0;
            z-index: 1;

            tr {
                background-color: #FFFFFF;
            }

            th {
                background-color: #FFFFFF;
                border: none;
                font-size: 0.82rem;
                font-weight: 600;
                color: #514F4E;
                padding: 0.4rem 0.75rem;
                vertical-align: middle;
            }
        }

        tbody {
            tr {
                background-color: #FFFFFF;
                box-shadow: 0px 1px 2px rgba(75, 75, 75, 0.12);
                border-radius: 4px;
            }

            td {
                border: none;
                padding: 0.65rem 0.75rem;
                vertical-align: middle;
                color: #514F4E;
                font-size: 0.85rem;
                background-color: #FFFFFF;
            }

            td:first-child {
                border-radius: 4px 0 0 4px;
            }

            td:last-child {
                border-radius: 0 4px 4px 0;
            }

            .col-email {
                font-size: 0.78rem;
                color: #7A7A7A;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }
`;
